import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import * as searchApi from '@/features/search/api';
import { Item } from '@/features/backlog/types';
import { taskTypeMeta, TASK_TYPES } from '@/features/backlog/constants';
import { useAppData } from '@/app-data';
import { TaskTypeGlyph, Search, X } from '@/components/ui/icons';
import { cn } from '@/components/ui/cn';
import { CanvasItemsStore } from './use-canvas-items';

/**
 * "Add item card" picker: search EXISTING backlog items (across boards) to drop a card that references
 * one, or CREATE a new item on a chosen board there and then. On confirm it hands back the item's id +
 * board so the canvas can place the card. Rendered as a screen-space modal (portaled to body).
 */
export default function ItemCardPicker({
  open,
  onClose,
  onPick,
  store,
  heading = 'Add item card',
}: {
  open: boolean;
  onClose: () => void;
  onPick: (itemId: string, boardId: string) => void;
  store: CanvasItemsStore;
  heading?: string;
}) {
  const { boards } = useAppData();
  const [tab, setTab] = useState<'existing' | 'new'>('existing');
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Item[]>([]);
  const [searching, setSearching] = useState(false);
  const seq = useRef(0);

  // New-item form.
  const [boardId, setBoardId] = useState('');
  const [title, setTitle] = useState('');
  const [type, setType] = useState('story');
  const [creating, setCreating] = useState(false);
  const boardById = useMemo(() => new Map(boards.map((b) => [b.id, b])), [boards]);

  useEffect(() => {
    if (!open) return;
    setTab('existing');
    setQ('');
    setResults([]);
    setTitle('');
    setBoardId(boards[0]?.id ?? '');
  }, [open, boards]);

  // Debounced cross-board search of existing items.
  useEffect(() => {
    if (!open || tab !== 'existing') return undefined;
    const s = ++seq.current;
    setSearching(true);
    const t = window.setTimeout(() => {
      searchApi
        .searchItems({ text: q.trim(), clauses: [], limit: 25 })
        .then((r) => { if (s === seq.current) setResults(r.data); })
        .catch(() => { if (s === seq.current) setResults([]); })
        .finally(() => { if (s === seq.current) setSearching(false); });
    }, 200);
    return () => window.clearTimeout(t);
  }, [q, open, tab]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const pick = (it: Item) => { onPick(it.id, it.board_id); onClose(); };
  const createAndPick = async () => {
    if (!boardId || !title.trim() || creating) return;
    setCreating(true);
    const it = await store.createItemOnBoard(boardId, { title: title.trim(), type: type as Item['type'] });
    setCreating(false);
    if (it) { onPick(it.id, it.board_id); onClose(); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-start justify-center bg-black/40 pt-[12vh]" onPointerDown={onClose}>
      <div className="w-[460px] max-w-[92vw] rounded-2xl border border-line bg-surface-raised shadow-2xl" onPointerDown={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-line px-3 py-2.5">
          <span className="text-[13px] font-semibold text-fg-strong">{heading}</span>
          <div className="ml-2 flex items-center gap-0.5 rounded-full bg-layer p-0.5">
            {(['existing', 'new'] as const).map((t) => (
              <button key={t} type="button" onClick={() => setTab(t)} className={cn('rounded-full px-2.5 py-1 text-[12px] font-semibold transition-colors', tab === t ? 'bg-surface-raised text-fg-strong shadow-sm' : 'text-fg-faint hover:text-fg-soft')}>
                {t === 'existing' ? 'Existing' : 'New item'}
              </button>
            ))}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="ml-auto flex h-7 w-7 items-center justify-center rounded-lg text-fg-faint hover:bg-layer hover:text-fg"><X className="h-4 w-4" /></button>
        </div>

        {tab === 'existing' ? (
          <div className="p-2.5">
            <div className="mb-2 flex h-9 items-center gap-2 rounded-xl border border-line bg-surface-inset px-3 focus-within:border-accent/50">
              <Search className="h-4 w-4 shrink-0 text-fg-faint" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items across all boards…" className="min-w-0 flex-1 bg-transparent text-[13px] text-fg placeholder:text-fg-ghost focus:outline-none" />
            </div>
            <div className="thin-scroll max-h-[340px] overflow-y-auto">
              {searching && results.length === 0 ? (
                <div className="py-8 text-center text-[12px] text-fg-faint">Searching…</div>
              ) : results.length === 0 ? (
                <div className="py-8 text-center text-[12px] text-fg-faint">No matching items.</div>
              ) : (
                results.map((it) => (
                  <button key={it.id} type="button" onClick={() => pick(it)} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-layer">
                    <TaskTypeGlyph type={it.type} color={taskTypeMeta(it.type).color} />
                    <span className="item-key shrink-0 rounded px-1 py-px tnum text-[10px] font-semibold">{it.key}</span>
                    <span className="min-w-0 flex-1 truncate text-[13px] text-fg-soft">{it.title || 'Untitled'}</span>
                    <span className="shrink-0 truncate rounded-full bg-layer-strong px-2 py-0.5 text-[10.5px] font-medium text-fg-faint">{boardById.get(it.board_id)?.name ?? ''}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3 p-3.5">
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-fg-faint">Board</label>
              <select value={boardId} onChange={(e) => setBoardId(e.target.value)} className="h-9 w-full rounded-lg border border-line bg-surface-inset px-2.5 text-[13px] text-fg-soft focus:border-accent/50 focus:outline-none">
                {boards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-fg-faint">Type</label>
              <div className="flex flex-wrap gap-1.5">
                {TASK_TYPES.map((t) => (
                  <button key={t.value} type="button" onClick={() => setType(t.value)} className={cn('flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] transition-colors', type === t.value ? 'border-accent/50 bg-layer text-fg' : 'border-line text-fg-faint hover:text-fg-soft')}>
                    <TaskTypeGlyph type={t.value} color={t.color} /> {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-fg-faint">Title</label>
              <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void createAndPick(); }} placeholder="What needs doing?" className="h-9 w-full rounded-lg border border-line bg-surface-inset px-2.5 text-[13px] text-fg focus:border-accent/50 focus:outline-none" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="rounded-lg px-3 py-1.5 text-[12.5px] text-fg-muted hover:bg-layer">Cancel</button>
              <button type="button" onClick={() => void createAndPick()} disabled={!boardId || !title.trim() || creating} className="rounded-lg bg-accent px-3.5 py-1.5 text-[12.5px] font-semibold text-ink disabled:opacity-40">
                {creating ? 'Creating…' : 'Create & add'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
